import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

const BOTTOM_PADDING = 8;
const GAP = 5;

/**
 * Returns a ref that keeps the element's bottom edge flush with the viewport bottom.
 *
 * Sets `maxHeight` so the element never overflows past the viewport.
 * When `stretch` is `true`, also sets `height` so the element fills all
 * remaining vertical space instead of just being capped.
 *
 * Recalculates on resize, parent resize, and intersection changes.
 *
 * @param stretch - If `true`, force the element to fill the remaining height (default `false`).
 */
export function useBottomAligned<T extends HTMLElement = HTMLDivElement>(stretch = false) {
  const ref = useRef<T>(null);

  const apply = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const top = Math.round(el.getBoundingClientRect().top);
    if (top <= 0) return;
    const value = `calc(100vh - ${top}px - ${BOTTOM_PADDING}px - ${GAP}px)`;
    el.style.maxHeight = value;
    if (stretch) {
      el.style.height = value;
    }
  }, [stretch]);

  useLayoutEffect(apply);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const timers: ReturnType<typeof setTimeout>[] = [];

    const scheduleRecalc = () => {
      timers.forEach(clearTimeout);
      timers.length = 0;
      timers.push(setTimeout(apply, 500));
    };

    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        apply();
        scheduleRecalc();
      }
    });
    io.observe(el);

    const ro = new ResizeObserver(apply);
    if (el.parentElement) {
      ro.observe(el.parentElement);
    }

    let resizeTimer: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(apply, 100);
    };
    window.addEventListener('resize', onResize);

    return () => {
      timers.forEach(clearTimeout);
      io.disconnect();
      ro.disconnect();
      window.removeEventListener('resize', onResize);
      clearTimeout(resizeTimer);
    };
  }, [apply]);

  return ref;
}
